/**
 * Sidebar icons (design agreed 29 Sep 2026): outline when inactive, filled
 * when active. Attributes is an ID card, not a tag. 24×24, currentColor.
 */

export type NavIconName = 'home' | 'courses' | 'templates' | 'library' | 'members' | 'attributes' | 'groups' | 'settings' | 'reports' | 'learning'

export function NavIcon({ name, active, size = 20 }: { name: NavIconName; active: boolean; size?: number }) {
  const line = { fill: 'none', stroke: 'currentColor', strokeWidth: active ? 1.9 : 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' } as const
  const solid = { fill: 'currentColor', stroke: 'currentColor', strokeWidth: 1.2, strokeLinejoin: 'round' } as const
  const s = active ? solid : line
  let body
  switch (name) {
    case 'home':
      body = active
        ? <path d="M3.5 10.3 12 3.4l8.5 6.9V20a1 1 0 0 1-1 1h-5v-5.5h-5V21h-5a1 1 0 0 1-1-1z" {...solid} />
        : <path d="M4 10.6 12 4l8 6.6V19.5a.9.9 0 0 1-.9.9H14.6v-5.3H9.4v5.3H4.9a.9.9 0 0 1-.9-.9z" {...line} />
      break
    case 'courses':
      body = active
        ? <><path d="M11.2 6.4C9.2 5 6.6 4.6 4 5.1v13.4c2.6-.4 5.2 0 7.2 1.3z" {...solid} /><path d="M12.8 6.4c2-1.4 4.6-1.8 7.2-1.3v13.4c-2.6-.4-5.2 0-7.2 1.3z" {...solid} /></>
        : <><path d="M12 6.6C10 5.1 7 4.7 4 5.3v13.2c3-.6 6-.2 8 1.3 2-1.5 5-1.9 8-1.3V5.3c-3-.6-6-.2-8 1.3z" {...line} /><path d="M12 6.6v13.2" {...line} /></>
      break
    case 'templates':
      body = <><rect x="4" y="4" width="16" height="6.5" rx="2" {...s} /><rect x="4" y="13" width="7" height="7" rx="2" {...s} /><rect x="13" y="13" width="7" height="7" rx="2" {...s} /></>
      break
    case 'library':
      body = <><rect x="4" y="4.5" width="3.6" height="15.5" rx="1" {...s} /><rect x="9.4" y="4.5" width="3.6" height="15.5" rx="1" {...s} /><path d="m14.7 5.4 3.3-.9 3.3 14.4-3.3.9z" {...s} /></>
      break
    case 'members':
      body = active
        ? <><circle cx="9" cy="8.3" r="3.3" {...solid} /><path d="M3 19.6c.4-3.6 2.9-5.8 6-5.8s5.6 2.2 6 5.8z" {...solid} /><circle cx="16.6" cy="8.6" r="2.6" {...solid} /><path d="M16.2 13.5c2.6-.1 4.5 1.9 4.9 5.2h-4.4c-.2-2-.8-3.7-1.9-4.9z" {...solid} /></>
        : <><circle cx="9" cy="8.3" r="3.1" {...line} /><path d="M3.5 19.3c.5-3.3 2.8-5.3 5.5-5.3s5 2 5.5 5.3" {...line} /><circle cx="16.6" cy="8.6" r="2.4" {...line} /><path d="M16.3 13.8c2.3 0 4 1.8 4.4 4.7" {...line} /></>
      break
    case 'attributes':
      // An ID card reads the same outlined or filled, so the active state keeps the outline, heavier.
      body = <><rect x="3" y="5" width="18" height="14" rx="2.5" {...line} /><circle cx="8.6" cy="10.4" r="1.8" {...line} /><path d="M5.9 16.1c.5-1.4 1.5-2.1 2.7-2.1s2.2.7 2.7 2.1" {...line} /><path d="M13.6 10.2h4.2M13.6 13.8h4.2" {...line} /></>
      break
    case 'groups':
      // Three people in a ring — a hand-picked group (User groups).
      body = <><circle cx="12" cy="7.2" r="2.6" {...(active ? solid : line)} /><circle cx="6.2" cy="15.6" r="2.6" {...(active ? solid : line)} /><circle cx="17.8" cy="15.6" r="2.6" {...(active ? solid : line)} /><path d="M9.6 9.6 7.8 13M14.4 9.6l1.8 3.4M9 16.4h6" {...line} /></>
      break
    case 'learning':
      // A mortarboard — courses to take.
      body = <><path d="M2.8 9.2 12 4.8l9.2 4.4L12 13.6z" {...s} /><path d="M6.5 11.2v4.3c1.4 1.4 3.4 2.2 5.5 2.2s4.1-.8 5.5-2.2v-4.3" {...line} /><path d="M21 9.4v4.8" {...line} /></>
      break
    case 'reports':
      // Three bars rising — how learning is going.
      body = <><rect x="4.5" y="12.5" width="3.6" height="7" rx="1" {...s} /><rect x="10.2" y="8.5" width="3.6" height="11" rx="1" {...s} /><rect x="15.9" y="4.5" width="3.6" height="15" rx="1" {...s} /></>
      break
    case 'settings':
      body = <><path d="M4 8h16M4 16h16" {...line} /><circle cx="15" cy="8" r="2.1" fill={active ? 'currentColor' : '#fff'} stroke="currentColor" strokeWidth={active ? 1.9 : 1.6} /><circle cx="9" cy="16" r="2.1" fill={active ? 'currentColor' : '#fff'} stroke="currentColor" strokeWidth={active ? 1.9 : 1.6} /></>
      break
  }
  return <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ flex: 'none' }}>{body}</svg>
}
